import { Node as CraftNode } from '@candulabs/craft/dist/core/nodes/types';
import React from 'react';

import Node from 'craft/components/Node';
import Placeholder from 'craft/components/Placeholder';
import { BuiltInComponents } from 'craft/constants';
import { useCss, useEditor, useNodeContext } from 'craft/hooks';
import { Resolver } from 'craft/types';
import { hasDescendants } from 'craft/utils';

interface Props {
  name: string;
  children?: React.ReactElement | React.ReactElement[];
}

export const ConditionalViewOption: Resolver = ({ name, children }: Props) => {
  const { id } = useNodeContext();
  const { query } = useEditor();
  // In the SDK, we don't render the flow step.
  // Doing that in craft is quite hard, so what we do is that we make the
  // flowstep have the same css as the flow, which achieve the same effect
  const { css: cssObj } = useCss([query.node(id).getParent().node.id]);
  const { flexGrow, alignSelf, flexBasis, flexShrink, ...otherCss } = cssObj ?? {};

  const hasChildren = hasDescendants(children);
  const placeholderComponent = <Placeholder placeholderText={`Option - ${name}`} />;
  return (
    <Node overrideCss={otherCss} applyNodeProps={false}>
      {hasChildren ? children : placeholderComponent}
    </Node>
  );
};

ConditionalViewOption.craft = {
  displayName: (node) => `Option - ${node.data.props.name}`,
  name: BuiltInComponents.ConditionalViewOption,
  isCanvas: true,
  rules: {
    canDrop: (node: CraftNode) => node.data.name === BuiltInComponents.ConditionalView,
    canDrag: () => false,
    canDelete: (node: CraftNode, helpers) => {
      const isLastRemaining = helpers(node.id).getParent().descendants().length === 1;
      return !isLastRemaining;
    },
    canMoveIn: (incomingNodes: CraftNode[]) =>
      incomingNodes.every(
        (node) =>
          ![BuiltInComponents.ConditionalView, BuiltInComponents.ConditionalViewOption].includes(
            node.data.name as BuiltInComponents,
          ),
      ),
  },
};

ConditionalViewOption.resize = {
  shouldDisableHandlers: true,
};

// Flow steps are not real nodes and shouldn't have any concrete toolbox options
ConditionalViewOption.toolbox = {
  hasOnlyFlowToolbox: true,
};
